import { forwardRef, useMergeRefs } from '@chakra-ui/react';
import { SelectOption } from '@pelicargo/types';
import { useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { ReactSelectProps } from '../utils/types';
import { ReactSelect } from './ReactSelect';
import { SimpleField, SimpleXProps } from './SimpleField';
import { useSimpleFormContext } from './SimpleForm';

type OptionDataTypes = string | number | boolean;

const convertSingleValToOption = (val: OptionDataTypes): SelectOption => ({
  label: String(val),
  value: String(val),
});
const convertSingleOptionToVal = (val: SelectOption) => val?.value;

const convertValToOption = (val: OptionDataTypes | OptionDataTypes[]): SelectOption | SelectOption[] => {
  if (Array.isArray(val)) return val.map(convertSingleValToOption);
  return convertSingleValToOption(val);
};

const convertOptionToVal = (val: SelectOption | SelectOption[]): OptionDataTypes | OptionDataTypes[] => {
  if (Array.isArray(val)) return val.map(convertSingleOptionToVal);
  return convertSingleOptionToVal(val);
};

type GroupOption = {
  label: string;
  options: SelectOption[];
};

type SimpleReactSelectProps = ReactSelectProps &
  SimpleXProps & {
    max?: number;
    defaultValue?: string;
    options?: SelectOption[];
    optionGroupOptions?: GroupOption[];
    isPortaled?: boolean;
    title?: string;
  };

export const SimpleReactSelect = forwardRef(
  ({ name, max, options, optionGroupOptions, label, helper, defaultValue, ...rest }: SimpleReactSelectProps, ref) => {
    const { simpleOnBlur } = useSimpleFormContext();
    const { control } = useFormContext();
    const {
      field: { onChange, value, ...fieldProps }, // Extract ref to avoid passing it to ReactSelect
    } = useController({ name, control });

    const localOnChange = (option: SelectOption) => {
      const val = convertOptionToVal(option);

      onChange(val === 'undefined' ? undefined : val);
      if ((rest?.ignoreOnBlur !== undefined && !rest.ignoreOnBlur) || rest?.ignoreOnBlur == undefined) {
        simpleOnBlur?.({ target: { name } } as any);
      }
    };

    const selectedOption = useMemo(() => {
      if (!value && !defaultValue) return null;
      if (!value && defaultValue) convertValToOption(defaultValue);

      const foundOption = options?.find((option: SelectOption) => String(option.value) === String(value)) as
        | SelectOption
        | undefined;
      if (foundOption) return foundOption;
      return convertValToOption(value);
    }, [options, value, defaultValue]);
    const mergedRef = useMergeRefs(ref, fieldProps.ref);

    return (
      <SimpleField name={name} label={label} helper={helper} title={rest?.title} ignoreOnBlur={rest?.ignoreOnBlur}>
        <ReactSelect
          {...rest}
          max={max}
          options={options || optionGroupOptions}
          onChange={localOnChange}
          value={selectedOption}
          {...fieldProps}
          ref={mergedRef}
        />
      </SimpleField>
    );
  },
);
